import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, ROLE_LABELS, SECTION_LABELS, VISIBILITY_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { X, Radio, Users2, Vote, Hand, Check, Mic, Square, FileSignature, Clock } from "lucide-react";

const DEFAULT_OPTS = "Sì\nNo\nAstenuto";

export default function LiveMeeting({ meetingId, onClose }) {
  const [st, setSt] = useState(null);
  const [draft, setDraft] = useState(null); // verbale precompilato dopo "Termina"
  const [pollQ, setPollQ] = useState("");
  const [pollOpts, setPollOpts] = useState(DEFAULT_OPTS);
  const [showPollForm, setShowPollForm] = useState(false);
  const [intTopic, setIntTopic] = useState("");
  const [intNote, setIntNote] = useState("");
  const [memberFilter, setMemberFilter] = useState("");
  const pollingRef = useRef(true);

  const apply = (data) => setSt(data);
  const load = async () => {
    if (!pollingRef.current) return;
    try { const r = await api.get(`/meetings/${meetingId}/live`); apply(r.data); } catch { /* noop */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t); // eslint-disable-next-line
  }, [meetingId]);

  const canManage = st?.can_manage;
  const poll = st?.poll;

  const togglePresence = async (uid, present) => {
    try { const r = await api.post(`/meetings/${meetingId}/live/presence`, { user_id: uid, present }); apply(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const openPoll = async () => {
    const options = pollOpts.split("\n").map(s => s.trim()).filter(Boolean);
    if (!pollQ.trim()) { toast.error("Scrivi la domanda"); return; }
    try {
      const r = await api.post(`/meetings/${meetingId}/live/poll`, { question: pollQ, options });
      apply(r.data); setShowPollForm(false); setPollQ(""); setPollOpts(DEFAULT_OPTS);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const closePoll = async () => {
    try { const r = await api.post(`/meetings/${meetingId}/live/poll/close`); apply(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const castVote = async (optId) => {
    try { const r = await api.post(`/meetings/${meetingId}/live/vote`, { option_id: optId }); apply(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const requestFloor = async () => {
    if (!intTopic.trim()) { toast.error("Indica l'argomento"); return; }
    try {
      const r = await api.post(`/meetings/${meetingId}/live/intervention`, { topic: intTopic, note: intNote });
      apply(r.data); setIntTopic(""); setIntNote(""); toast.success("Richiesta inviata al Segretario");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const decideInt = async (iid, action) => {
    try { const r = await api.post(`/meetings/${meetingId}/live/intervention/${iid}/decide`, { action }); apply(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const endMeeting = async () => {
    if (!window.confirm("Terminare la riunione e precompilare il verbale del giorno?")) return;
    pollingRef.current = false;
    try {
      const r = await api.post(`/meetings/${meetingId}/live/end`);
      const d = r.data.verbale_draft;
      setDraft({ ...d, present_text: (d.present || []).join("\n") });
      apply(r.data.state);
    } catch (e) { pollingRef.current = true; toast.error(e.response?.data?.detail || "Errore"); }
  };
  const saveVerbale = async () => {
    const present = (draft.present_text || "").split("\n").map(s => s.trim()).filter(Boolean);
    try {
      await api.post("/verbali", {
        title: draft.title, date: draft.date, section: draft.section,
        present, agenda: draft.agenda, decisions: draft.decisions,
        poll_id: null, visibility: draft.visibility,
      });
      toast.success("Verbale salvato in Trasparenza → Verbali");
      close();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio verbale"); }
  };
  const close = () => { pollingRef.current = false; onClose?.(); };

  const options = poll?.options || [];
  const maxCount = Math.max(1, ...options.map(o => (poll?.counts?.[o.id] || 0)));
  const members = (st?.eligible_members || []).filter(m =>
    !memberFilter || (m.name || "").toLowerCase().includes(memberFilter.toLowerCase()));
  const queue = st?.interventions || [];

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-[#1C2420]/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-0 sm:p-4 lg:p-6" data-testid="live-meeting-overlay">
      <div className="w-full max-w-5xl min-h-full sm:min-h-0 bg-[#FAF7F2] sm:rounded-2xl shadow-2xl sm:my-2">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#2B3A33] text-white sm:rounded-t-2xl px-4 sm:px-5 py-3 sm:py-4 flex items-center gap-2 sm:gap-3 flex-wrap">
          <span className="relative flex h-3 w-3">
            {st?.status === "live" && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EF3E36] opacity-75"></span>}
            <span className={`relative inline-flex rounded-full h-3 w-3 ${st?.status === "live" ? "bg-[#EF3E36]" : "bg-[#8A8272]"}`}></span>
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-white/70 flex items-center gap-1"><Radio className="w-3 h-3" /> Riunione live · {SECTION_LABELS[st?.meeting?.section] || st?.meeting?.section}</div>
            <div className="font-display text-lg font-bold truncate">{st?.meeting?.title || "—"}</div>
          </div>
          <span className="text-xs bg-white/15 rounded-full px-3 py-1 inline-flex items-center gap-1"><Users2 className="w-3 h-3" /> {st?.present_count || 0} presenti</span>
          {canManage && st?.status === "live" && (
            <button data-testid="live-end-btn" onClick={endMeeting} className="text-xs font-semibold bg-[#EF3E36] hover:bg-[#C9302A] rounded-lg px-3 py-1.5 inline-flex items-center gap-1"><Square className="w-3 h-3" /> Termina</button>
          )}
          <button data-testid="live-close-btn" onClick={close} className="p-1.5 rounded-lg hover:bg-white/15"><X className="w-4 h-4" /></button>
        </div>

        {draft ? (
          <VerbaleDraft draft={draft} setDraft={setDraft} onSave={saveVerbale} onCancel={close} />
        ) : (
          <div className="p-3 sm:p-6 grid md:grid-cols-2 gap-4 sm:gap-5">
            {/* LEFT: Presenze (manager) o stato (socio) + Votazione */}
            <div className="space-y-4 sm:space-y-5">
              {canManage ? (
                <div className="paper rounded-2xl p-4" data-testid="live-presence-panel">
                  <div className="font-display text-base font-bold text-[#2B3A33] mb-2 flex items-center gap-2"><Users2 className="w-4 h-4 text-[#EF3E36]" /> Presenti alla riunione</div>
                  <p className="text-xs text-[#8A8272] mb-3">Spunta chi è presente (solo soci abilitati per <b>{SECTION_LABELS[st?.meeting?.section]}</b>).</p>
                  <input data-testid="live-member-filter" value={memberFilter} onChange={e => setMemberFilter(e.target.value)} placeholder="Cerca socio…" className="w-full mb-2 px-3 py-2 rounded-lg border border-[#E8E2D8] text-sm" />
                  <div className="max-h-72 overflow-y-auto divide-y divide-[#F0EADF]">
                    {members.map(m => (
                      <label key={m.user_id} data-testid={`live-member-${m.user_id}`} className="flex items-center gap-3 py-2 cursor-pointer">
                        <input type="checkbox" checked={!!m.present} onChange={e => togglePresence(m.user_id, e.target.checked)} className="w-4 h-4 accent-[#EF3E36]" data-testid={`live-present-toggle-${m.user_id}`} />
                        <span className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-[#2B3A33] block truncate">{m.name}</span>
                          <span className="text-[10px] uppercase tracking-wide text-[#8A8272]">{ROLE_LABELS[m.role] || m.role}</span>
                        </span>
                        {m.present && <Check className="w-4 h-4 text-[#16A34A]" />}
                      </label>
                    ))}
                    {members.length === 0 && <div className="text-sm text-[#8A8272] py-3">Nessun socio abilitato trovato.</div>}
                  </div>
                </div>
              ) : (
                <div className="paper rounded-2xl p-4" data-testid="live-presence-status">
                  <div className="font-display text-base font-bold text-[#2B3A33] flex items-center gap-2"><Users2 className="w-4 h-4 text-[#EF3E36]" /> La tua presenza</div>
                  {st?.is_present
                    ? <div className="mt-2 text-sm text-[#166534] bg-[#DCFCE7] rounded-lg px-3 py-2 inline-flex items-center gap-2"><Check className="w-4 h-4" /> Sei segnato come presente: puoi votare e chiedere la parola.</div>
                    : <div className="mt-2 text-sm text-[#78350F] bg-[#FEF3C7] rounded-lg px-3 py-2">In attesa che il Segretario ti segni presente.</div>}
                </div>
              )}

              {/* Votazione */}
              <div className="paper rounded-2xl p-4" data-testid="live-poll-panel">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="font-display text-base font-bold text-[#2B3A33] flex items-center gap-2"><Vote className="w-4 h-4 text-[#EF3E36]" /> Votazione live</div>
                  {canManage && st?.status === "live" && (
                    poll?.open
                      ? <button data-testid="live-poll-close-btn" onClick={closePoll} className="text-xs font-semibold border border-[#E8E2D8] bg-white rounded-lg px-3 py-1.5">Chiudi votazione</button>
                      : <button data-testid="live-poll-open-btn" onClick={() => setShowPollForm(v => !v)} className="terracotta-btn text-xs font-semibold rounded-lg px-3 py-1.5">Apri votazione</button>
                  )}
                </div>

                {canManage && showPollForm && !poll?.open && (
                  <div className="space-y-2 mb-3 bg-white rounded-xl p-3 border border-[#E8E2D8]">
                    <input data-testid="live-poll-question" value={pollQ} onChange={e => setPollQ(e.target.value)} placeholder="Domanda (es. Approvazione bilancio 2026)" className="w-full px-3 py-2 rounded-lg border border-[#E8E2D8] text-sm" />
                    <textarea data-testid="live-poll-options" value={pollOpts} onChange={e => setPollOpts(e.target.value)} rows={3} className="w-full px-3 py-2 rounded-lg border border-[#E8E2D8] text-sm font-mono" />
                    <div className="text-[10px] text-[#8A8272]">Un'opzione per riga.</div>
                    <button data-testid="live-poll-submit" onClick={openPoll} className="terracotta-btn text-xs font-semibold rounded-lg px-4 py-2">Avvia votazione</button>
                  </div>
                )}

                {poll ? (
                  <div className="space-y-2">
                    <div className="font-semibold text-[#2B3A33] text-sm">{poll.question} {!poll.open && <span className="text-[10px] uppercase bg-[#E8E2D8] text-[#2B3A33] rounded px-1.5 py-0.5 ml-1">Chiusa</span>}</div>
                    {options.map(o => {
                      const c = poll.counts?.[o.id] || 0;
                      const mine = poll.my_vote === o.id;
                      return (
                        <button
                          key={o.id}
                          data-testid={`live-vote-${o.id}`}
                          disabled={!poll.open || !st?.is_present}
                          onClick={() => castVote(o.id)}
                          className={`w-full text-left rounded-xl border px-3 py-2 transition ${mine ? "border-[#EF3E36] bg-[#FDF2F2]" : "border-[#E8E2D8] bg-white"} ${poll.open && st?.is_present ? "hover:border-[#EF3E36] cursor-pointer" : "cursor-default"}`}
                        >
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium text-[#2B3A33] inline-flex items-center gap-1">{mine && <Check className="w-3.5 h-3.5 text-[#EF3E36]" />}{o.text}</span>
                            <span className="font-bold text-[#2B3A33]">{c}</span>
                          </div>
                          <div className="mt-1 h-1.5 rounded-full bg-[#F0EADF] overflow-hidden">
                            <div className="h-full bg-[#EF3E36]" style={{ width: `${(c / maxCount) * 100}%` }} />
                          </div>
                          {canManage && (poll.voters?.[o.id]?.length > 0) && (
                            <div className="mt-1 text-[10px] text-[#8A8272]">{poll.voters[o.id].join(", ")}</div>
                          )}
                        </button>
                      );
                    })}
                    <div className="text-xs text-[#8A8272]">{poll.total} voti espressi{!st?.is_present && poll.open ? " · solo i presenti possono votare" : ""}</div>
                  </div>
                ) : (
                  <div className="text-sm text-[#8A8272]">Nessuna votazione attiva.</div>
                )}
              </div>
            </div>

            {/* RIGHT: Interventi */}
            <div className="paper rounded-2xl p-4 flex flex-col" data-testid="live-interventions-panel">
              <div className="font-display text-base font-bold text-[#2B3A33] mb-2 flex items-center gap-2"><Hand className="w-4 h-4 text-[#EF3E36]" /> Richieste di intervento</div>

              {!canManage && st?.is_present && st?.status === "live" && (
                <div className="bg-white rounded-xl p-3 border border-[#E8E2D8] space-y-2 mb-3" data-testid="live-intervention-form">
                  <input data-testid="live-int-topic" value={intTopic} onChange={e => setIntTopic(e.target.value)} placeholder="Argomento" className="w-full px-3 py-2 rounded-lg border border-[#E8E2D8] text-sm" />
                  <textarea data-testid="live-int-note" value={intNote} onChange={e => setIntNote(e.target.value)} rows={2} placeholder="Cosa vuoi dire (facoltativo)" className="w-full px-3 py-2 rounded-lg border border-[#E8E2D8] text-sm" />
                  <button data-testid="live-int-submit" onClick={requestFloor} className="terracotta-btn text-xs font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-1"><Hand className="w-3 h-3" /> Chiedi la parola</button>
                </div>
              )}

              <div className="flex-1 space-y-2 max-h-[26rem] overflow-y-auto">
                {queue.length === 0 && <div className="text-sm text-[#8A8272]">Nessuna richiesta.</div>}
                {queue.map((i, idx) => (
                  <div key={i.id} data-testid={`live-int-${i.id}`} className={`rounded-xl border px-3 py-2 ${i.status === "approved" ? "border-[#16A34A] bg-[#F0FDF4]" : i.status === "rejected" ? "border-[#E8E2D8] bg-[#F7F4EE] opacity-70" : i.status === "done" ? "border-[#E8E2D8] bg-white opacity-80" : "border-[#E8E2D8] bg-white"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#2B3A33] truncate">#{idx + 1} · {i.name}</div>
                        <div className="text-xs text-[#6B6558]">{i.topic}</div>
                      </div>
                      <StatusBadge status={i.status} />
                    </div>
                    {i.note && <div className="mt-1 text-xs text-[#6B6558] italic">«{i.note}»</div>}
                    {canManage && st?.status === "live" && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {i.status === "requested" && <>
                          <button data-testid={`live-int-approve-${i.id}`} onClick={() => decideInt(i.id, "approve")} className="text-[11px] font-semibold bg-[#16A34A] text-white rounded-lg px-2.5 py-1 inline-flex items-center gap-1"><Mic className="w-3 h-3" /> Dai la parola</button>
                          <button data-testid={`live-int-reject-${i.id}`} onClick={() => decideInt(i.id, "reject")} className="text-[11px] font-semibold border border-[#E8E2D8] text-[#EF3E36] rounded-lg px-2.5 py-1">Rifiuta</button>
                        </>}
                        {i.status === "approved" && <button data-testid={`live-int-done-${i.id}`} onClick={() => decideInt(i.id, "done")} className="text-[11px] font-semibold border border-[#E8E2D8] text-[#2B3A33] rounded-lg px-2.5 py-1">Concluso</button>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function StatusBadge({ status }) {
  const map = {
    requested: { t: "In coda", c: "bg-[#FEF3C7] text-[#92400E]", I: Clock },
    approved: { t: "In parola", c: "bg-[#DCFCE7] text-[#166534]", I: Mic },
    rejected: { t: "Rifiutato", c: "bg-[#FEE2E2] text-[#991B1B]", I: X },
    done: { t: "Concluso", c: "bg-[#E8E2D8] text-[#2B3A33]", I: Check },
  };
  const s = map[status] || map.requested;
  const I = s.I;
  return <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 inline-flex items-center gap-1 shrink-0 ${s.c}`}><I className="w-3 h-3" /> {s.t}</span>;
}

function VerbaleDraft({ draft, setDraft, onSave, onCancel }) {
  const up = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  return (
    <div className="p-4 sm:p-6 space-y-3" data-testid="live-verbale-draft">
      <div className="flex items-center gap-2 text-[#2B3A33]">
        <FileSignature className="w-5 h-5 text-[#EF3E36]" />
        <div className="font-display text-xl font-bold">Verbale precompilato del giorno</div>
      </div>
      <p className="text-sm text-[#6B6558]">Rivedi e correggi, poi salva. Finirà in Trasparenza → Verbali con sigillo digitale.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="vdraft-title" value={draft.title} onChange={e => up("title", e.target.value)} className="px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
        <input data-testid="vdraft-date" type="date" value={draft.date} onChange={e => up("date", e.target.value)} className="px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <select data-testid="vdraft-section" value={draft.section} onChange={e => up("section", e.target.value)} className="px-4 py-2.5 rounded-lg border border-[#E8E2D8] bg-white">
          {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select data-testid="vdraft-visibility" value={draft.visibility} onChange={e => up("visibility", e.target.value)} className="px-4 py-2.5 rounded-lg border border-[#E8E2D8] bg-white">
          {Object.entries(VISIBILITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <textarea data-testid="vdraft-present" value={draft.present_text} onChange={e => up("present_text", e.target.value)} rows={4} placeholder="Presenti (uno per riga)" className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
      <textarea data-testid="vdraft-agenda" value={draft.agenda} onChange={e => up("agenda", e.target.value)} rows={3} placeholder="Ordine del giorno" className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
      <textarea data-testid="vdraft-decisions" value={draft.decisions} onChange={e => up("decisions", e.target.value)} rows={8} placeholder="Decisioni / esiti votazioni / interventi" className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8] font-mono text-sm" />
      <div className="flex gap-2">
        <button data-testid="vdraft-save" onClick={onSave} className="terracotta-btn px-5 py-2.5 rounded-xl font-semibold">Salva verbale</button>
        <button data-testid="vdraft-skip" onClick={onCancel} className="px-4 py-2.5 rounded-xl border border-[#E8E2D8]">Chiudi senza salvare</button>
      </div>
    </div>
  );
}
